<script lang="ts">
  import { REG, byId } from './lib/data/grimoire';
  import { store } from './lib/store.svelte';
  import { reveal } from './lib/actions';
  import { makeShortcuts } from './lib/shortcuts';
  import { parseFilter, buildFilterURL } from './lib/url-filter';

  import Rail from './lib/components/Rail.svelte';
  import Hero from './lib/components/Hero.svelte';
  import Legend from './lib/components/Legend.svelte';
  import Find from './lib/components/Find.svelte';
  import Chapter from './lib/components/Chapter.svelte';
  import Currents from './lib/components/Currents.svelte';
  import Coda from './lib/components/Coda.svelte';
  import Sheet from './lib/components/Sheet.svelte';
  import EmbersCanvas from './lib/components/EmbersCanvas.svelte';
  import GlowDefs from './lib/components/GlowDefs.svelte';
  import HelpOverlay from './lib/components/HelpOverlay.svelte';
  import KeyboardHint from './lib/components/KeyboardHint.svelte';
  import ScrollTop from './lib/components/ScrollTop.svelte';

  // Deep-link integration: hash → open entry; popstate → close.
  //
  // When the page loads with a hash, the browser already jumped the viewport
  // to where that element lives on the page. We don't want that — we're
  // showing a modal, not scrolling. store.open() snapshots the current
  // scrollY for restore-on-close; we override that snapshot to 0 here so
  // closing lands at the top of the page (the user came in via a share link,
  // not via the page).
  function handleHashFromLoad() {
    const h = (location.hash || '').slice(1);
    if (h && byId[h]) {
      // Clear the hash so subsequent manual back doesn't reopen.
      history.replaceState({}, '', location.pathname + location.search);
      store.open(h);
      // Override restore target: hash-loaded user hasn't seen the page yet.
      store._overrideScrollRestore(0);
      // The body-lock $effect has already run by the time `load` fires, so
      // window.scrollTo is frozen. Temporarily release the lock, jump, then
      // the effect re-applies on the next reactive flush.
      const prev = document.body.style.overflow;
      document.body.style.overflow = '';
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.body.style.overflow = prev;
    }
  }

  function onPopState() {
    const id = (location.hash || '').slice(1);
    if (id && byId[id]) store.open(id, { viaPop: true });
    else store.close({ viaPop: true });

    // Re-apply filter from URL on back/forward too.
    const f = parseFilter();
    store.query = f.q;
    store.setReg(f.reg);
    if (store.trainOnly !== f.train) store.toggleTrain();
  }

  // Apply filter state from URL on first load.
  function applyFilterFromURL() {
    const f = parseFilter();
    if (f.q) store.query = f.q;
    if (f.reg !== 'all') store.setReg(f.reg);
    if (f.train) store.toggleTrain();
  }
  applyFilterFromURL();

  // Take ownership of scroll restoration. Otherwise back/forward through
  // hash entries would jump the viewport to where the user was when they
  // opened the modal — disorienting on this kind of long page.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  // When the store opens/closes a sheet, sync the URL.
  //
  // - First open of an entry: pushState (so back closes the modal).
  // - Subsequent in-modal navigation (prev/next/sibling): replaceState, so
  //   walking across N entries produces a single back press to exit.
  // - Close: replaceState to clear the hash atomically — avoids the previous
  //   `history.back()` approach, which fought with stacked pushState entries.
  let lastTrackedId: string | null = null;
  $effect(() => {
    const id = store.openId;
    if (id && id !== lastTrackedId) {
      const viaPop = store._consumeOpenViaPop();
      const replace = store._consumeOpenReplace();
      lastTrackedId = id;
      if (viaPop) return;
      const url = `${location.pathname}${location.search}#${id}`;
      if (replace) history.replaceState({ entry: id }, '', url);
      else history.pushState({ entry: id }, '', url);
    } else if (!id && lastTrackedId) {
      const viaPop = store._consumeOpenViaPop();
      lastTrackedId = null;
      if (viaPop) return;
      if (location.hash) {
        // Replace current entry with a clean URL instead of pushing more.
        history.replaceState({}, '', `${location.pathname}${location.search}`);
      }
    }
  });

  // Reveal-on-filter: when filtering starts, force-reveal any pending cards.
  $effect(() => {
    if (store.hasFilter) {
      document
        .querySelectorAll<HTMLElement>('#chapters .reveal:not(.seen)')
        .forEach((el) => el.classList.add('seen'));
    }
  });

  // Scroll the sheet to top whenever a new entry opens.
  let lastSeen: string | null = null;
  $effect(() => {
    const id = store.openId;
    if (id && id !== lastSeen) {
      lastSeen = id;
      const sheet = document.querySelector('.sheet');
      if (sheet) sheet.scrollTop = 0;
    } else if (!id) {
      lastSeen = null;
    }
  });

  // Body scroll lock while sheet/help is open — prevents background scroll
  // leaking through, which feels janky on touchpads and phones.
  $effect(() => {
    const open = !!store.openId || store.helpOpen;
    if (typeof document === 'undefined') return;
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  });

  // Sync filter state to URL query params. Debounced so typing doesn't spam
  // history entries.
  let filterSyncTimer: ReturnType<typeof setTimeout> | null = null;
  let lastQuery = '';
  let lastReg = 'all';
  let lastTrain = false;
  let filterInitialized = false;
  $effect(() => {
    // Read filter state — these reads create the reactive dependency.
    const q = store.query;
    const reg = store.activeReg;
    const train = store.trainOnly;

    if (!filterInitialized) {
      // Skip initial sync so we don't rewrite the URL we just read.
      lastQuery = q;
      lastReg = reg;
      lastTrain = train;
      filterInitialized = true;
      return;
    }
    if (q === lastQuery && reg === lastReg && train === lastTrain) return;
    lastQuery = q;
    lastReg = reg;
    lastTrain = train;

    if (filterSyncTimer) clearTimeout(filterSyncTimer);
    filterSyncTimer = setTimeout(() => {
      const u = buildFilterURL({ q, reg, train });
      const want = u.search + u.hash;
      const have = location.search + location.hash;
      if (want !== have) {
        history.pushState({}, '', u);
      }
    }, 350);
  });

  // Global keyboard shortcuts.
  const onKeydown = makeShortcuts({
    focusSearch: () => {
      const el = document.getElementById('findInput') as HTMLInputElement | null;
      if (!el) return;
      if (store.openId) store.close();
      store.toggleHelp(false);
      el.focus();
      el.select();
    },
    jumpTo: (id: string) => {
      if (store.openId) store.close();
      store.toggleHelp(false);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    },
    chapters: () => REG.map((r) => r.id),
    help: () => store.toggleHelp(),
    random: () => {
      const pool: string[] = [];
      for (const r of REG) for (const e of r.entries) if (store.isVisible(e.id)) pool.push(e.id);
      if (!pool.length) return;
      const id = pool[Math.floor(Math.random() * pool.length)];
      if (store.openId) store.close();
      store.toggleHelp(false);
      store.open(id);
    },
  });
</script>

<GlowDefs />
<EmbersCanvas />
<Rail />

<main id="chapters">
  <Hero />
  <Legend />
  <Find />
  {#each REG as chapter (chapter.id)}
    <Chapter {chapter} />
  {/each}
  {#if !store.anyVisible}
    <div class="wrap">
      <div class="find__empty">
        {#if store.query.trim()}
          Ничего не нашлось по «<b>{store.query.trim()}</b>».
        {:else}
          Ничего не найдено с текущими фильтрами.
        {/if}
        <br />
        <button class="find__reset" type="button" onclick={() => store.resetFilter()}>
          сбросить фильтры
        </button>
      </div>
    </div>
  {/if}
  <!-- When the search returns zero results, dim the unrelated sections
       rather than leaving them floating beside an empty chapters area. -->
  <Currents dimmed={!store.anyVisible} />
  <Coda dimmed={!store.anyVisible} />
</main>

<Sheet />
<HelpOverlay />
<KeyboardHint />
<ScrollTop />

<svelte:window on:popstate={onPopState} on:load={handleHashFromLoad} onkeydown={onKeydown} />
